import { apiErrorMessage } from "@/lib/apiErrorMessage";
import i18n from "@/i18n";
import { useEffect, useState } from "react";
import { Plus, KeyRound, Trash2, Ban, Check, Pencil, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const PLATFORM_ROLES = [
    { value: "super_admin", get label() { return i18n.t("admin.l.Super_Admin", "Super Admin"); },
      get hint() { return i18n.t("admin.l.Full_platform_control", "Full platform control."); } },
    { value: "admin", get label() { return i18n.t("admin.l.Platform_Admin", "Platform Admin"); },
      get hint() { return i18n.t("admin.l.Everything_except_demoting_or_removing_other_Super_Admins", "Everything except demoting or removing other Super Admins."); } },
    { value: "sales", get label() { return i18n.t("admin.l.Sales", "Sales"); },
      get hint() { return i18n.t("admin.l.Companies_subscriptions_invoices_and_sales_dashboards", "Companies, subscriptions, invoices and sales dashboards."); } },
    { value: "support", get label() { return i18n.t("admin.l.Support", "Support"); },
      get hint() { return i18n.t("admin.l.Read_only_access_to_help_customers_troubleshoot", "Read-only access to help customers troubleshoot."); } },
];

/**
 * PlatformUsers — Super Admin only.
 *
 * Dedicated page to manage the 4 platform-side accounts (super_admin,
 * admin, sales, support). Customer-tenant accounts are managed from the
 * existing Users tab in the Super Admin portal — this page shows only
 * platform accounts (no company_id).
 */
export default function PlatformUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | user obj
    const [resetting, setResetting] = useState(null); // user obj
    const [tempPassword, setTempPassword] = useState(null); // {email, password}

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users", {
                params: { platform_only: true },
            });
            const platform = data.filter((u) =>
                ["super_admin", "admin", "sales", "support"].includes(u.role)
                || ["super_admin", "admin", "sales", "support"].includes(u.profile_role));
            setRows(platform);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const disable = async (u) => {
        if (!window.confirm(i18n.t("admin.confirmDisable", { defaultValue: "Disable {{email}}?", email: u.email, interpolation: { escapeValue: false } }))) return;
        try {
            await api.patch(`/admin/users/${u.id}`, { active: false });
            toast.success(`${u.email} disabled`);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    const enable = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { active: true });
            toast.success(`${u.email} reactivated`);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    const remove = async (u) => {
        if (!window.confirm(i18n.t("admin.confirmDelete", { defaultValue: "Delete {{email}}? This cannot be undone.", email: u.email, interpolation: { escapeValue: false } }))) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success(`${u.email} deleted`);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    const resetPassword = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
            setTempPassword({ email: u.email, password: data.temporary_password });
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="platform-users-page">
            <PageHeader
                title={i18n.t("admin.l.Platform_users", "Platform users")}
                description={i18n.t("admin.l.Super_Admin_Platform_Admin_Sales_and_Support_accounts", "Super Admin, Platform Admin, Sales and Support accounts.")}
                action={
                    <button data-testid="platform-users-new"
                            onClick={() => setEditing("new")}
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800">
                        <Plus className="h-4 w-4" /> {i18n.t("admin.l.New_platform_user", "New platform user")}
                    </button>
                }
            />

            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="platform-users-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{i18n.t("admin.l.Name", "Name")}</th>
                            <th className="px-5 py-3 font-semibold">{i18n.t("admin.l.Email", "Email")}</th>
                            <th className="px-5 py-3 font-semibold">{i18n.t("admin.l.Role", "Role")}</th>
                            <th className="px-5 py-3 font-semibold">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {loading && (
                            <tr><td colSpan={5} className="px-5 py-6 text-neutral-500 text-sm">{i18n.t("admin.l.Loading", "Loading…")}</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={5} className="px-5 py-6 text-neutral-500 text-sm">{i18n.t("admin.l.No_platform_users_yet", "No platform users yet.")}</td></tr>
                        )}
                        {rows.map((u) => {
                            const roleLabel = PLATFORM_ROLES.find((r) => r.value === (u.profile_role || u.role))?.label
                                || u.role;
                            const isActive = u.active !== false && !u.locked_at;
                            return (
                                <tr key={u.id} data-testid={`platform-user-${u.id}`}>
                                    <td className="px-5 py-3 font-medium">{u.name}</td>
                                    <td className="px-5 py-3 font-mono text-xs text-neutral-700">{u.email}</td>
                                    <td className="px-5 py-3">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 border border-neutral-200 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
                                            <ShieldCheck className="h-3 w-3" /> {roleLabel}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                            isActive ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                                                     : "bg-neutral-100 text-neutral-500 border border-neutral-200"
                                        }`}>
                                            {isActive ? i18n.t("admin.l.Active", "Active") : i18n.t("admin.l.Disabled", "Disabled")}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap space-x-1">
                                        <button data-testid={`platform-user-edit-${u.id}`}
                                                title={i18n.t("admin.l.Edit", "Edit")}
                                                onClick={() => setEditing(u)}
                                                className="h-8 w-8 grid place-items-center rounded hover:bg-neutral-100">
                                            <Pencil className="h-4 w-4 text-neutral-600" />
                                        </button>
                                        <button data-testid={`platform-user-reset-${u.id}`}
                                                title={i18n.t("admin.l.Reset_password", "Reset password")}
                                                onClick={() => resetPassword(u)}
                                                className="h-8 w-8 grid place-items-center rounded hover:bg-neutral-100">
                                            <KeyRound className="h-4 w-4 text-neutral-600" />
                                        </button>
                                        {isActive ? (
                                            <button data-testid={`platform-user-disable-${u.id}`}
                                                    title={i18n.t("admin.l.Disable", "Disable")}
                                                    onClick={() => disable(u)}
                                                    className="h-8 w-8 grid place-items-center rounded hover:bg-amber-50">
                                                <Ban className="h-4 w-4 text-amber-700" />
                                            </button>
                                        ) : (
                                            <button data-testid={`platform-user-enable-${u.id}`}
                                                    title={i18n.t("admin.l.Reactivate", "Reactivate")}
                                                    onClick={() => enable(u)}
                                                    className="h-8 w-8 grid place-items-center rounded hover:bg-emerald-50">
                                                <Check className="h-4 w-4 text-emerald-700" />
                                            </button>
                                        )}
                                        <button data-testid={`platform-user-delete-${u.id}`}
                                                title={i18n.t("admin.l.Delete", "Delete")}
                                                onClick={() => remove(u)}
                                                className="h-8 w-8 grid place-items-center rounded hover:bg-red-50">
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <PlatformUserDialog
                open={editing !== null}
                user={typeof editing === "object" ? editing : null}
                onClose={() => setEditing(null)}
                onSaved={(temp) => {
                    setEditing(null);
                    load();
                    if (temp) setTempPassword(temp);
                }}
            />

            <Dialog open={!!tempPassword} onOpenChange={(o) => { if (!o) setTempPassword(null); }}>
                <DialogContent className="max-w-md" data-testid="platform-users-temp-password">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("admin.l.Temporary_password", "Temporary password")}</DialogTitle>
                        <DialogDescription>
                            Share this password with <span className="font-mono">{tempPassword?.email}</span> — it
                            is shown once and cannot be recovered later.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md bg-neutral-900 text-white p-3 text-center font-mono text-lg tracking-wide"
                         data-testid="platform-users-temp-password-value">
                        {tempPassword?.password}
                    </div>
                    <button onClick={() => setTempPassword(null)}
                            className="mt-2 h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                        Done
                    </button>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PlatformUserDialog({ open, user, onClose, onSaved }) {
    const isEdit = !!user;
    const [form, setForm] = useState({
        name: "", email: "", phone: "", role: "sales", active: true,
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) {
            if (user) setForm({
                name: user.name || "", email: user.email || "",
                phone: user.phone || "",
                role: user.profile_role || user.role || "sales",
                active: user.active !== false,
            });
            else setForm({ name: "", email: "", phone: "", role: "sales", active: true });
        }
    }, [open, user]);

    const save = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error(i18n.t("admin.l.Name_and_email_are_required", "Name and email are required")); return;
        }
        setBusy(true);
        try {
            if (isEdit) {
                await api.patch(`/admin/users/${user.id}`, {
                    name: form.name.trim(),
                    email: form.email.trim().toLowerCase(),
                    phone: form.phone.trim() || null,
                    role: form.role,
                    active: form.active,
                });
                toast.success(i18n.t("admin.l.User_updated", "User updated"));
                onSaved(null);
            } else {
                const { data } = await api.post("/admin/users", {
                    name: form.name.trim(),
                    email: form.email.trim().toLowerCase(),
                    phone: form.phone.trim() || null,
                    role: form.role,
                    status: form.active ? "active" : "suspended",
                });
                toast.success(i18n.t("admin.l.Platform_user_created", "Platform user created"));
                onSaved(data.temporary_password
                    ? { email: form.email.trim().toLowerCase(),
                         password: data.temporary_password }
                    : null);
            }
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="platform-user-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? i18n.t("admin.l.Edit_platform_user", "Edit platform user") : i18n.t("admin.l.New_platform_user", "New platform user")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.l.Platform_users_are_the_MiseOne_team_Super_Admin_Platform_Admin_Sales_S", "Platform users are the MiseOne team — Super Admin, Platform Admin, Sales, Support.")}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={save} className="space-y-3">
                    <div>
                        <Label>{i18n.t("admin.l.First_and_last_name", "First and last name")}</Label>
                        <input data-testid="platform-user-name"
                               className="ms-input" value={form.name}
                               onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                        <Label>{i18n.t("admin.l.Email", "Email")}</Label>
                        <input data-testid="platform-user-email" type="email"
                               className="ms-input font-mono text-sm" value={form.email}
                               onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                        <Label>{i18n.t("admin.l.Phone_optional", "Phone (optional)")}</Label>
                        <input data-testid="platform-user-phone"
                               className="ms-input" value={form.phone}
                               onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div>
                        <Label>{i18n.t("admin.l.Role", "Role")}</Label>
                        <select data-testid="platform-user-role"
                                className="ms-input" value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}>
                            {PLATFORM_ROLES.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                        <p className="mt-1 text-[11px] text-neutral-500">
                            {PLATFORM_ROLES.find((r) => r.value === form.role)?.hint}
                        </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input data-testid="platform-user-active"
                               type="checkbox" checked={form.active}
                               onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                        {i18n.t("admin.l.Active", "Active")}
                    </label>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose}
                                className="h-10 px-4 rounded-md border border-neutral-300 bg-white text-sm font-semibold">
                            {i18n.t("admin.l.Cancel", "Cancel")}
                        </button>
                        <button type="submit" disabled={busy}
                                data-testid="platform-user-save"
                                className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                            {busy ? i18n.t("admin.l.Saving", "Saving…") : isEdit ? i18n.t("admin.l.Save_changes", "Save changes") : i18n.t("admin.l.Create_user", "Create user")}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Label({ children }) {
    return (
        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">
            {children}
        </label>
    );
}
